import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import {
  Mail, MailOpen, RefreshCw, Loader2, ArrowDownLeft, ArrowUpRight,
  CheckCircle2, XCircle, AlertTriangle, HelpCircle, ExternalLink,
} from "lucide-react";

const STATUS_CHIP = {
  sent:       { label: "Sent",       color: "bg-status-green-dim text-status-green border-green-200",      Icon: CheckCircle2 },
  queued:     { label: "Queued",     color: "bg-sky-50 text-sky-700 border-sky-200",                       Icon: Loader2 },
  disabled:   { label: "Disabled",   color: "bg-slate-50 text-slate-600 border-slate-200",                 Icon: AlertTriangle },
  failed:     { label: "Failed",     color: "bg-status-red-dim text-status-red border-red-200",            Icon: XCircle },
  received:   { label: "Received",   color: "bg-emerald-50 text-emerald-700 border-emerald-200",           Icon: MailOpen },
  unmatched:  { label: "Unmatched",  color: "bg-amber-50 text-amber-700 border-amber-200",                 Icon: HelpCircle },
};

const TEMPLATE_LABEL = {
  ob_invite:                "OB invitation",
  // ob_invite_pipeline retained for historical audit rows written before
  // PR E (20 May 2026); new sends use the harmonised 'ob_invite' key.
  ob_invite_pipeline:       "OB invitation",
  ob_resend:                "OB resend",
  ob_remind:                "OB reminder",
  ob_confirmation:          "OB confirmation + care pack",
  ob_joint_invite:          "Joint OB invite",
  ob_joint_confirmation:    "Joint OB confirmation",
  care_pack:                "CCL care pack",
  conflict_approval:        "Conflict approval (internal)",
  ccl_signed_internal:      "CCL signed (internal)",
  postintake_filing_alert:  "Post-intake filing alert (internal)",
  inbound:                  "Inbound reply",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function StatusChip({ status }) {
  const cfg = STATUS_CHIP[status] || { label: status || "Unknown", color: "bg-slate-50 text-slate-500 border-slate-200", Icon: HelpCircle };
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md border ${cfg.color}`} data-testid={`comm-status-${status}`}>
      <Icon className={`w-3 h-3 ${status === "queued" ? "animate-spin" : ""}`} />
      {cfg.label}
    </span>
  );
}

function templateLabel(row) {
  if (row.direction === "received") return TEMPLATE_LABEL.inbound;
  return TEMPLATE_LABEL[row.template_key] || row.template_key || "Send";
}

export default function CommunicationsPanel({ matterReference }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    if (!matterReference) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/email-audit/by-matter/${encodeURIComponent(matterReference)}`,
        { params: { limit: 200 } },
      );
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (e) {
      setError(errorMessage(e, "Failed to load communications"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [matterReference]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const visibleRows = rows.filter((r) => {
    if (r.direction === "received") return true;
    return Boolean(r.client_email);
  });

  const sentCount = visibleRows.filter((r) => r.direction === "sent").length;
  const receivedCount = visibleRows.filter((r) => r.direction === "received").length;
  const failedCount = visibleRows.filter((r) => r.status === "failed").length;
  const unmatchedCount = visibleRows.filter((r) => r.status === "unmatched").length;

  return (
    <div className="rounded-md border border-navy-border bg-[rgba(26,37,71,0.6)] p-4" data-testid="communications-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-teal" />
          <h2 className="text-sm font-bold text-teal uppercase tracking-wide">Communications</h2>
          <span className="text-xs text-slate-400">
            ({sentCount} sent, {receivedCount} received
            {failedCount > 0 ? `, ${failedCount} failed` : ""}
            {unmatchedCount > 0 ? `, ${unmatchedCount} unmatched` : ""})
          </span>
        </div>
        <button
          onClick={fetchRows}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-teal border border-teal/40 rounded hover:bg-teal/10"
          data-testid="communications-refresh"
          disabled={loading}
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {error && (
        <div
          className="mb-3 px-3 py-2 text-xs rounded-md bg-status-red-dim border border-red-300 text-status-red"
          data-testid="communications-error"
        >
          {error}
        </div>
      )}

      {!error && !loading && visibleRows.length === 0 && (
        <div
          className="px-3 py-4 text-xs text-slate-400 italic"
          data-testid="communications-empty"
        >
          No emails recorded for this matter yet.
        </div>
      )}

      {visibleRows.length > 0 && (
        <ul className="divide-y divide-navy-border/60" data-testid="communications-list">
          {visibleRows.map((r, idx) => {
            const isReceived = r.direction === "received";
            const Arrow = isReceived ? ArrowDownLeft : ArrowUpRight;
            const isFailed = r.status === "failed";
            const isUnmatched = r.status === "unmatched";
            const rowClasses = [
              "py-2",
              isFailed ? "border-l-2 border-l-status-red pl-2" : "",
              isUnmatched ? "border-l-2 border-l-amber-400 pl-2 bg-amber-50/5" : "",
            ].filter(Boolean).join(" ");
            return (
              <li
                key={r.message_id || r.graph_message_id || `${r.sent_at}-${idx}`}
                className={rowClasses}
                data-testid={`communications-row-${r.direction}-${idx}`}
              >
                <div className="flex items-start gap-2">
                  <Arrow className={`w-3.5 h-3.5 mt-1 flex-shrink-0 ${isReceived ? "text-emerald-400" : "text-teal"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-slate-100 truncate max-w-xs" title={r.subject}>
                        {r.subject || "(no subject)"}
                      </span>
                      <StatusChip status={r.status} />
                      {!isReceived && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300 border border-slate-600">
                          {templateLabel(r)}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {isReceived ? (
                        <>From <span className="text-slate-200">{r.client_email || r.from_address || "(unknown)"}</span></>
                      ) : (
                        <>To <span className="text-slate-200">{(r.to_addresses || []).join(", ") || r.client_email || "(unknown)"}</span>
                          {r.cc_addresses?.length ? <>, cc {r.cc_addresses.join(", ")}</> : null}
                        </>
                      )}
                      <span className="mx-1.5 text-slate-500">·</span>
                      <span>{formatDate(r.sent_at)}</span>
                      {r.web_link && (
                        <>
                          <span className="mx-1.5 text-slate-500">·</span>
                          <a
                            href={r.web_link}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-teal hover:underline"
                            data-testid={`communications-weblink-${idx}`}
                          >
                            Open in Outlook <ExternalLink className="w-3 h-3" />
                          </a>
                        </>
                      )}
                    </div>
                    {isFailed && r.error && (
                      <div className="text-[11px] text-status-red mt-1" data-testid={`communications-error-text-${idx}`}>
                        Error: {r.error}
                      </div>
                    )}
                    {isUnmatched && r.unmatched_reason && (
                      <div className="text-[11px] text-amber-400 mt-1" data-testid={`communications-unmatched-reason-${idx}`}>
                        Unmatched: {r.unmatched_reason}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
